import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { FilterChipIcon } from './filter-chip-icon';
import { FilterChipLabel } from './filter-chip-label';
import { FilterChipRoot } from './filter-chip-root';
import type { FilterChipPropsInterface } from './types/filter-chip.types';

/**
 * Filter chips are used to trigger actions related to pieces of content.
 * Avoid using them as CTAs or for navigation (ex: as tabs).
 *
 * @related ['Button', 'AssistChip', 'InputChip']
 * @since 8.97.0
 */

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipPropsInterface>(
  (props: FilterChipPropsInterface, forwardedRef) => {
    const { children, icon, truncate, ...rest } = props;

    return (
      <FilterChip.Root ref={forwardedRef} {...rest}>
        {icon ? <FilterChip.Icon>{icon}</FilterChip.Icon> : null}
        <FilterChip.Label truncate={truncate}>{String(children)}</FilterChip.Label>
      </FilterChip.Root>
    );
  },
) as ForwardRefExoticComponent<FilterChipPropsInterface> & {
  Root: typeof FilterChipRoot;
  Icon: typeof FilterChipIcon;
  Label: typeof FilterChipLabel;
};

FilterChip.displayName = 'FilterChip';

FilterChip.Root = FilterChipRoot;
FilterChip.Icon = FilterChipIcon;
FilterChip.Label = FilterChipLabel;
